import { BuildingPeriodAccountingExpense } from '@/app/models/accountingExpense';
import ActionMenu, { ItemAction } from '@/app/utils/ActionMenu';
import formatterMonetaryValue from '@/app/utils/numberUtils';
import DeleteIcon from '@mui/icons-material/Delete';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Tooltip,
  Typography,
} from '@mui/material';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

type ConfigureExpenseTableProps = {
  expenses: BuildingPeriodAccountingExpense[];
  handleDelete: (id: number) => void;
};

function ConfigureExpenseTable(props: ConfigureExpenseTableProps) {
  const { expenses, handleDelete } = props;

  const { t } = useTranslation(['configureExpense', 'common']);

  const [order, setOrder] = useState<'asc' | 'desc'>('desc');
  const [orderBy, setOrderBy] = useState('value');

  const handleRequestSortGoals = (property: string) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const visibleRows = useMemo(() => {
    if (!expenses) return [];
    const copiedExpenses = [...expenses];
    return copiedExpenses.sort((a, b) => {
      const isAsc = order === 'asc';
      return isAsc ? a.value - b.value : b.value - a.value;
    });
  }, [expenses, order]);

  return (
    <div
      style={{
        display: 'flex',
        width: '100%',
        marginTop: '24px',
        marginBottom: '16px',
        justifyContent: 'space-around',
      }}
    >
      <div style={{ width: '100%' }}>
        <TableContainer sx={{ maxHeight: '25rem' }}>
          <Table stickyHeader size="small" aria-label="Expense Table">
            <TableHead sx={{ fontWeight: 700 }}>
              <TableRow>
                <TableCell align="left" style={{ fontWeight: 'bold' }}>
                  {t('expense')}
                </TableCell>
                <TableCell align="left" style={{ fontWeight: 'bold' }}>
                  {t('supplier')}
                </TableCell>
                <TableCell align="left" style={{ fontWeight: 'bold' }}>
                  {t('release.date')}
                </TableCell>
                <TableCell align="left" style={{ fontWeight: 'bold' }}>
                  <TableSortLabel
                    active={orderBy === 'value'}
                    direction={orderBy === 'value' ? order : 'asc'}
                    onClick={() => handleRequestSortGoals('value')}
                    style={{ fontWeight: 'bold' }}
                  >
                    {t('value')}
                  </TableSortLabel>
                </TableCell>
                <TableCell align="left" style={{ fontWeight: 'bold' }}>
                  {t('common:actions')}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {expenses &&
                visibleRows.map((row) => (
                  <TableRow key={row.id}>
                    <TableCell>{`${row.code} - ${row.description}`}</TableCell>
                    <TableCell>
                      <Tooltip
                        arrow
                        enterDelay={200}
                        placement="top-end"
                        componentsProps={{
                          tooltip: {
                            sx: {
                              bgcolor: '#F7F7F7',
                              minWidth: '30rem',
                              border: '1px solid #D9D9D9',
                              color: 'black',
                              '& .MuiTooltip-arrow': {
                                color: '#F7F7F7',
                                '&:before': {
                                  border: '1px solid #D9D9D9',
                                },
                              },
                            },
                          },
                        }}
                        title={row.supplier}
                      >
                        <span>{`${row.supplier.slice(0, 70)}...`}</span>
                      </Tooltip>
                    </TableCell>
                    <TableCell>{row.registerDate}</TableCell>
                    <TableCell>{`R$ ${formatterMonetaryValue(row.value)}`}</TableCell>
                    <TableCell>
                      <ActionMenu>
                        <ItemAction
                          text={t('delete')}
                          Icon={DeleteIcon}
                          onClick={() => {
                            handleDelete(row.id);
                          }}
                        />
                      </ActionMenu>
                    </TableCell>
                  </TableRow>
                ))}
              {expenses?.length === 0 && (
                <TableRow>
                  <TableCell sx={{ textAlign: 'center' }} colSpan={8}>
                    {t('common:no.results.found')}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <div style={{ width: '100%', display: 'flex', flexDirection: 'row-reverse', marginTop: '1rem' }}>
          <Typography sx={{ fontWeight: 'bold', alignSelf: 'end' }}>
            {`${t('Total')}: R$ ${formatterMonetaryValue(expenses?.reduce((acc, item) => acc + item.value, 0) || 0)}`}
          </Typography>
        </div>
      </div>
    </div>
  );
}

export default ConfigureExpenseTable;
